import { Sparkles, Send, Wand2 } from "lucide-react";

/**
 * A premium, hand-built "product preview" mock — not a screenshot.
 * Mimics the shape of the MACHA dashboard: composer + AI rewrite panel.
 * Uses only neutral tokens + one indigo accent.
 */
export function ProductPreview() {
  return (
    <div className="relative mx-auto w-full max-w-5xl">
      {/* Frame */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)]">
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-muted" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted" />
          </div>
          <div className="hidden items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-[11px] text-muted-foreground sm:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
            app.replyai.com / proposals / new
          </div>
          <div className="w-[44px]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px]">
          {/* Composer side */}
          <div className="border-b border-border p-6 md:border-b-0 md:border-r">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  Upwork · Job invitation
                </p>
                <h3 className="mt-1 text-[15px] font-semibold tracking-tight text-foreground">
                  Senior React developer for a 6-week SaaS rebuild
                </h3>
              </div>
              <span className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
                Draft
              </span>
            </div>

            <div className="mt-5 space-y-3 rounded-xl border border-border bg-background p-4 text-[13px] leading-relaxed text-foreground/90">
              <p>Hi Sarah,</p>
              <p>
                Your job post stood out — particularly the part about
                rebuilding the dashboard without losing the existing customer
                workflows. I&apos;ve led two migrations like this for SaaS
                companies in the 50–200k MRR range.
              </p>
              <p>
                A few quick thoughts before we hop on a call:
              </p>
              <ul className="ml-4 list-disc space-y-1 text-foreground/80">
                <li>Ship behind a feature flag, route by route</li>
                <li>Keep your existing Tailwind tokens to avoid a redesign</li>
                <li>Wire analytics on day one to catch regressions</li>
              </ul>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Tone matched: <span className="text-foreground">Confident, warm</span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  tabIndex={-1}
                  className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Regenerate
                </button>
                <button
                  type="button"
                  tabIndex={-1}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-[11px] font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <Send className="h-3 w-3" /> Send proposal
                </button>
              </div>
            </div>
          </div>

          {/* AI panel */}
          <div className="bg-background/60 p-6">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                Copilot
              </p>
              <span className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                <Wand2 className="h-3 w-3" /> AI
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {[
                {
                  label: "Make it more confident",
                  hint: "Lift hedging language",
                },
                {
                  label: "Shorten by 30%",
                  hint: "Keep the 3 strongest points",
                },
                {
                  label: "Add a tactful price anchor",
                  hint: "Inserts a $4.5k range line",
                },
                {
                  label: "Follow-up in 3 days",
                  hint: "Schedules a polite nudge",
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="group flex items-start justify-between rounded-xl border border-border bg-card px-3 py-2.5 transition-colors hover:border-primary/40"
                >
                  <div>
                    <p className="text-[12.5px] font-medium text-foreground">
                      {row.label}
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {row.hint}
                    </p>
                  </div>
                  <span className="mt-0.5 text-[11px] text-muted-foreground transition-colors group-hover:text-primary">
                    ⌘↵
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-dashed border-border bg-background/60 px-3 py-2.5">
              <p className="text-[11px] text-muted-foreground">
                Win-rate estimate
              </p>
              <div className="mt-1.5 flex items-center justify-between">
                <p className="text-base font-semibold text-foreground">
                  72%
                </p>
                <div className="h-1.5 w-28 overflow-hidden rounded-full bg-muted">
                  <div className="h-full w-[72%] rounded-full bg-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Soft ground shadow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-12 -bottom-6 h-12 rounded-[100%] bg-foreground/10 blur-2xl"
      />
    </div>
  );
}
